import React from 'react';
import { Share2, Download, Search, RefreshCw, AlertTriangle, Shield, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const SocialMediaMonitoring = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing social media data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Share2 className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Social Media Monitoring</h1>
            <p className="text-sm text-gray-400">Monitor and protect your brand across social media platforms</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Monitored Accounts', value: '156', trend: '+12', icon: Share2 },
          { label: 'Brand Mentions', value: '2.4K', trend: '+89', icon: AlertTriangle },
          { label: 'Impersonations', value: '8', trend: '-2', icon: Shield },
          { label: 'Engagement', value: '45K', trend: '+1.2K', icon: ExternalLink }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Platform Monitoring */}
      <div className="grid grid-cols-2 gap-6">
        {/* Platform Activity */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Platform Activity</h2>
          <div className="space-y-4">
            {[
              { platform: 'Twitter', mentions: 856, sentiment: 'positive', engagement: '12K' },
              { platform: 'LinkedIn', mentions: 342, sentiment: 'neutral', engagement: '8K' },
              { platform: 'Facebook', mentions: 567, sentiment: 'positive', engagement: '15K' },
              { platform: 'Instagram', mentions: 423, sentiment: 'neutral', engagement: '10K' }
            ].map((platform) => (
              <div key={platform.platform} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{platform.platform}</div>
                  <div className="text-xs text-gray-400 mt-1">{platform.mentions} mentions</div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm font-medium text-white">{platform.engagement}</div>
                    <div className="text-xs text-gray-400">engagement</div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    platform.sentiment === 'positive' ? 'bg-emerald-500/10 text-emerald-500' :
                    'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {platform.sentiment}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Mentions */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Mentions</h2>
          <div className="space-y-4">
            {[
              { platform: 'Twitter', content: 'Great experience with @brand today!', sentiment: 'positive', time: '10m ago' },
              { platform: 'Facebook', content: 'Having issues with @brand service', sentiment: 'negative', time: '30m ago' },
              { platform: 'LinkedIn', content: 'Excited to partner with @brand', sentiment: 'positive', time: '1h ago' }
            ].map((mention, index) => (
              <div key={index} className="p-4 bg-gray-800/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">{mention.platform}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    mention.sentiment === 'positive' ? 'bg-emerald-500/10 text-emerald-500' :
                    'bg-red-500/10 text-red-500'
                  }`}>
                    {mention.sentiment}
                  </span>
                </div>
                <p className="text-sm text-white mb-2">{mention.content}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{mention.time}</span>
                  <Button variant="outline" size="sm">View</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sentiment Analysis */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Sentiment Analysis</h2>
        <div className="grid grid-cols-3 gap-6">
          {[
            { category: 'Positive Mentions', count: 856, percentage: 65, trend: 'up' },
            { category: 'Neutral Mentions', count: 342, percentage: 25, trend: 'stable' },
            { category: 'Negative Mentions', count: 134, percentage: 10, trend: 'down' }
          ].map((sentiment) => (
            <div key={sentiment.category} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">{sentiment.category}</span>
                <span className={`text-xs ${
                  sentiment.trend === 'up' ? 'text-emerald-500' :
                  sentiment.trend === 'down' ? 'text-red-500' :
                  'text-gray-500'
                }`}>
                  {sentiment.trend === 'up' ? '↑' :
                   sentiment.trend === 'down' ? '↓' :
                   '→'}
                </span>
              </div>
              <div className="text-2xl font-bold text-white mb-2">{sentiment.count}</div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    sentiment.category.includes('Positive') ? 'bg-emerald-500' :
                    sentiment.category.includes('Neutral') ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${sentiment.percentage}%` }}
                />
              </div>
              <div className="text-xs text-gray-500 mt-2">{sentiment.percentage}% of total mentions</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialMediaMonitoring;